// src/components/Timeline/TimelineChronology.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
   Box,
   Typography,
   Paper,
   Divider,
   Button,
   Chip,
   useMediaQuery,
   useTheme,
   IconButton,
   Tooltip,
   Fade,
   Zoom,
   Grow,
   Card,
   CardMedia,
   CardContent,
   CardActionArea,
   Badge,
   Avatar,
   AvatarGroup,
} from '@mui/material';
import {
   Timeline,
   TimelineItem,
   TimelineSeparator,
   TimelineConnector,
   TimelineContent,
   TimelineDot,
   TimelineOppositeContent,
} from '@mui/lab';
import {
   Today as TodayIcon,
   Hotel as HotelIcon,
   DirectionsCar as TransportIcon,
   Restaurant as FoodIcon,
   Hiking as ActivityIcon,
   Flight as FlightIcon,
   Attractions as AttractionsIcon,
   PhotoCamera as PhotoIcon,
   Map as MapIcon,
   Image as ImageIcon,
   Star as StarIcon,
   Groups as GroupsIcon,
   ArrowForward as ArrowForwardIcon,
   Explore as ExploreIcon,
   Place as PlaceIcon,
   AccessTime as AccessTimeIcon,
   Info as InfoIcon,
   PeopleAlt as PeopleAltIcon,
   LocalAirport as LocalAirportIcon,
   FlightTakeoff as FlightTakeoffIcon,
   FlightLand as FlightLandIcon,
   TrainOutlined as TrainIcon,
   DirectionsBus as BusIcon,
   DirectionsBoat as FerryIcon,
   Celebration as CelebrationIcon,
} from '@mui/icons-material';
import {
   format,
   isSameDay,
   differenceInDays,
   isToday,
   isPast,
   isFirstDayOfMonth,
   isBefore,
} from 'date-fns';
import { Activity, Accommodation, Transportation, TimelineEvent } from './types';

interface TimelineChronologyProps {
   days: Date[];
   isPastTrip: boolean;
   tripName?: string;
   startDate: Date;
   endDate: Date;
   getActivitiesForDay: (day: Date) => Activity[];
   getAccommodationsForDay: (day: Date) => Accommodation[];
   getTransportationForDay: (day: Date) => Transportation[];
   openImageDialog: (images: string[], startIndex?: number) => void;
   onDaySelect: (index: number) => void;
   collaborators?: { id: string; name: string; avatar?: string }[];
}

// Helper function to get a travel icon based on transport type
const getTransportIcon = (type?: string) => {
   switch (type) {
      case 'flight':
         return <FlightIcon />;
      case 'train':
         return <TrainIcon />;
      case 'bus':
         return <BusIcon />;
      case 'ferry':
         return <FerryIcon />;
      default:
         return <TransportIcon />;
   }
};

// Helper function to get an activity icon based on activity type or tags
const getActivityIcon = (activity: Activity) => {
   // Check type first
   if (activity.type === 'accommodation') {
      return <HotelIcon />;
   } else if (activity.type === 'transportation') {
      return <TransportIcon />;
   } else if (activity.type === 'food') {
      return <FoodIcon />;
   }

   // Otherwise check tags
   const tags = activity.tags?.map((t) => t.toLowerCase()) || [];

   if (tags.includes('food') || tags.includes('restaurant') || tags.includes('dining')) {
      return <FoodIcon />;
   } else if (tags.includes('hotel') || tags.includes('accommodation') || tags.includes('stay')) {
      return <HotelIcon />;
   } else if (tags.includes('attraction') || tags.includes('sight') || tags.includes('museum')) {
      return <AttractionsIcon />;
   } else if (tags.includes('activity') || tags.includes('tour')) {
      return <ActivityIcon />;
   }

   // Default icon
   return <ActivityIcon />;
};

const TimelineChronology: React.FC<TimelineChronologyProps> = ({
   days,
   isPastTrip,
   tripName = 'Trip',
   startDate,
   endDate,
   getActivitiesForDay,
   getAccommodationsForDay,
   getTransportationForDay,
   openImageDialog,
   onDaySelect,
   collaborators = [],
}) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.down('md'));

   // State for the element to scroll to
   const [highlightedDay, setHighlightedDay] = useState<number | null>(null);
   const dayRefs = useRef<(HTMLDivElement | null)[]>([]);
   const [showConfetti, setShowConfetti] = useState(false);

   // Scroll to today or trip start
   useEffect(() => {
      const todayIndex = days.findIndex((day) => isToday(day));

      if (todayIndex >= 0) {
         // Today is part of the trip
         setHighlightedDay(todayIndex);
      } else if (!isPast(startDate)) {
         // Trip hasn't started yet, highlight first day
         setHighlightedDay(0);
      } else if (isPast(endDate)) {
         // Trip has ended, highlight the last day with some fun confetti
         setHighlightedDay(days.length - 1);
         // Show confetti for past trips
         if (isPastTrip) {
            setShowConfetti(true);
            // Hide confetti after 3 seconds
            setTimeout(() => setShowConfetti(false), 3000);
         }
      }
   }, [days, startDate, endDate, isPastTrip]);

   // Scroll to highlighted day
   useEffect(() => {
      if (highlightedDay !== null && dayRefs.current[highlightedDay]) {
         dayRefs.current[highlightedDay]?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
         });
      }
   }, [highlightedDay]);

   // Calculate trip duration for display
   const tripDuration = differenceInDays(endDate, startDate) + 1;

   // Determine if this is a milestone day (like first day, last day, etc.)
   const isMilestoneDay = (day: Date, index: number) => {
      return (
         index === 0 || // First day
         index === days.length - 1 || // Last day
         isToday(day) || // Today
         isFirstDayOfMonth(day)
      ); // First day of a month
   };

   return (
      <Box sx={{ position: 'relative' }}>
         {/* Confetti animation for completed trips */}
         {showConfetti && (
            <Box
               sx={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 100,
                  pointerEvents: 'none',
                  background:
                     'radial-gradient(circle, rgba(255,255,255,0) 70%, rgba(255,255,255,0.2) 100%)',
                  animation: 'fadeOut 3s forwards',
                  '@keyframes fadeOut': {
                     '0%': { opacity: 1 },
                     '100%': { opacity: 0 },
                  },
                  '@keyframes moveDown': {
                     '0%': { transform: 'translateY(-100%)' },
                     '100%': { transform: 'translateY(100%)' },
                  },
               }}
            >
               <Box
                  sx={{
                     position: 'absolute',
                     width: '100%',
                     height: '100%',
                     backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Ccircle cx='10' cy='10' r='5' fill='%23ffd700'/%3E%3C/svg%3E\"), url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Ccircle cx='10' cy='10' r='3' fill='%2300bcd4'/%3E%3C/svg%3E\"), url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E%3Ccircle cx='10' cy='10' r='4' fill='%239c27b0'/%3E%3C/svg%3E\")",
                     animation: 'moveDown 3s linear',
                  }}
               />
            </Box>
         )}

         {/* Trip overview card */}
         <Paper
            elevation={3}
            sx={{
               p: 2,
               mb: 4,
               borderRadius: 2,
               backgroundImage: 'linear-gradient(to right, #f6f7f8, #e9ebee)',
               position: 'relative',
               overflow: 'hidden',
            }}
         >
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '30%',
                  height: '100%',
                  opacity: 0.05,
               }}
            >
               <ExploreIcon sx={{ width: '100%', height: '100%' }} />
            </Box>

            <Typography variant='h5' gutterBottom color='primary'>
               {tripName} Timeline
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
               <Box>
                  <Typography variant='body2' color='text.secondary'>
                     Duration
                  </Typography>
                  <Typography variant='h6'>
                     {tripDuration} {tripDuration === 1 ? 'Day' : 'Days'}
                  </Typography>
               </Box>

               <Box>
                  <Typography variant='body2' color='text.secondary'>
                     From - To
                  </Typography>
                  <Typography variant='h6'>
                     {format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}
                  </Typography>
               </Box>

               {collaborators.length > 0 && (
                  <Box sx={{ ml: 'auto' }}>
                     <Typography variant='body2' color='text.secondary' gutterBottom>
                        Traveling With
                     </Typography>
                     <AvatarGroup max={5} sx={{ justifyContent: 'flex-end' }}>
                        {collaborators.map((person) => (
                           <Tooltip key={person?.id} title={person?.name}>
                              <Avatar src={person?.avatar} alt={person?.name}>
                                 {person?.name?.charAt(0)}
                              </Avatar>
                           </Tooltip>
                        ))}
                     </AvatarGroup>
                  </Box>
               )}
            </Box>
         </Paper>

         {/* Trip progress visualization */}
         <Box sx={{ mb: 4, display: isPastTrip ? 'none' : 'block' }}>
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
               <Typography variant='subtitle2' color='text.secondary'>
                  Trip Progress
               </Typography>
               <Typography variant='subtitle2' color='text.secondary'>
                  {Math.min(
                     100,
                     Math.max(
                        0,
                        Math.round((differenceInDays(new Date(), startDate) / tripDuration) * 100)
                     )
                  )}
                  %
               </Typography>
            </Box>

            <Box
               sx={{
                  height: 8,
                  bgcolor: 'grey.200',
                  borderRadius: 4,
                  overflow: 'hidden',
               }}
            >
               <Box
                  sx={{
                     height: '100%',
                     width: `${Math.min(
                        100,
                        Math.max(
                           0,
                           Math.round(
                              (differenceInDays(new Date(), startDate) / tripDuration) * 100
                           )
                        )
                     )}%`,
                     bgcolor: 'primary.main',
                     borderRadius: 4,
                     transition: 'width 1s ease-in-out',
                  }}
               />
            </Box>
         </Box>

         {/* Main Timeline */}
         <Timeline position={isMobile ? 'right' : 'alternate'} sx={{ p: 0 }}>
            {days.map((day, index) => {
               const dayActivities = getActivitiesForDay(day);
               const dayAccommodations = getAccommodationsForDay(day);
               const dayTransportation = getTransportationForDay(day);

               const hasEvents =
                  dayActivities.length > 0 ||
                  dayAccommodations.length > 0 ||
                  dayTransportation.length > 0;

               const isHighlighted = isToday(day);
               const isPast = isBefore(day, new Date());
               const _isMilestone = isMilestoneDay(day, index);

               // Get all images for this day
               const dayImages = [
                  ...dayActivities.flatMap((a) => a.images || []),
                  ...dayAccommodations.flatMap((a) => a.images || []),
                  ...dayTransportation.flatMap((t) => t.images || []),
               ];

               // Calculate active status
               let status: 'past' | 'today' | 'future' = isPast
                  ? 'past'
                  : isToday(day)
                  ? 'today'
                  : 'future';

               return (
                  <TimelineItem
                     key={day.toISOString()}
                     ref={(el) => (dayRefs.current[index] = el as HTMLDivElement)}
                     sx={{
                        minHeight: _isMilestone ? 180 : 120,
                        opacity: status === 'past' ? 0.8 : 1,
                     }}
                  >
                     {/* Date information */}
                     {!isMobile && (
                        <TimelineOppositeContent sx={{ m: 'auto 0', maxWidth: 150 }}>
                           <Grow
                              in={true}
                              style={{ transformOrigin: '0 0 0' }}
                              timeout={500 + index * 100}
                           >
                              <Box>
                                 <Typography
                                    variant='h6'
                                    color={isHighlighted ? 'primary.main' : 'inherit'}
                                 >
                                    {format(day, 'EEE, MMM d')}
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary'>
                                    {format(day, 'yyyy')}
                                 </Typography>
                                 {_isMilestone && index === 0 && (
                                    <Chip
                                       label='Trip Start'
                                       color='primary'
                                       size='small'
                                       icon={<FlightTakeoffIcon />}
                                       sx={{ mt: 1 }}
                                    />
                                 )}
                                 {_isMilestone && index === days.length - 1 && (
                                    <Chip
                                       label='Trip End'
                                       color='secondary'
                                       size='small'
                                       icon={<FlightLandIcon />}
                                       sx={{ mt: 1 }}
                                    />
                                 )}
                              </Box>
                           </Grow>
                        </TimelineOppositeContent>
                     )}

                     {/* Timeline dot and connector */}
                     <TimelineSeparator>
                        <Zoom in={true} style={{ transitionDelay: `${50 * index}ms` }}>
                           <TimelineDot
                              color={isHighlighted ? 'secondary' : hasEvents ? 'primary' : 'grey'}
                              variant={
                                 isHighlighted || _isMilestone
                                    ? 'filled'
                                    : hasEvents
                                    ? 'filled'
                                    : 'outlined'
                              }
                              sx={{
                                 transition: 'all 0.3s',
                                 transform: isHighlighted ? 'scale(1.2)' : 'scale(1)',
                              }}
                           >
                              {isHighlighted ? (
                                 <TodayIcon />
                              ) : _isMilestone ? (
                                 index === 0 ? (
                                    <FlightTakeoffIcon />
                                 ) : index === days.length - 1 ? (
                                    <FlightLandIcon />
                                 ) : (
                                    <CelebrationIcon />
                                 )
                              ) : (
                                 <TodayIcon />
                              )}
                           </TimelineDot>
                        </Zoom>
                        <TimelineConnector />
                     </TimelineSeparator>

                     {/* Content */}
                     <TimelineContent sx={{ py: '12px', px: { xs: 1, sm: 2 } }}>
                        <Grow
                           in={true}
                           style={{ transformOrigin: '0 0 0' }}
                           timeout={700 + index * 100}
                        >
                           <Paper
                              elevation={_isMilestone || isHighlighted ? 6 : hasEvents ? 3 : 1}
                              sx={{
                                 p: { xs: 2, sm: 3 },
                                 border: isHighlighted
                                    ? '2px solid'
                                    : _isMilestone
                                    ? '1px solid'
                                    : 'none',
                                 borderColor: isHighlighted ? 'secondary.main' : 'primary.main',
                                 borderRadius: 2,
                                 transition: 'all 0.3s',
                                 '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: 6,
                                 },
                                 backgroundColor: isHighlighted
                                    ? 'rgba(225, 0, 80, 0.05)'
                                    : 'background.paper',
                              }}
                           >
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                 }}
                              >
                                 {/* Mobile date display */}
                                 {isMobile && (
                                    <Typography
                                       variant='subtitle1'
                                       sx={{ mr: 1, fontWeight: 'bold' }}
                                    >
                                       {format(day, 'EEE, MMM d')}
                                    </Typography>
                                 )}

                                 {/* Day status chip */}
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       ml: isMobile ? 0 : 'auto',
                                    }}
                                 >
                                    {isHighlighted ? (
                                       <Chip
                                          label='Today'
                                          color='secondary'
                                          size='small'
                                          icon={<TodayIcon />}
                                       />
                                    ) : index === 0 ? (
                                       <Chip
                                          label='First Day'
                                          color='primary'
                                          size='small'
                                          icon={<FlightTakeoffIcon />}
                                       />
                                    ) : index === days.length - 1 ? (
                                       <Chip
                                          label='Last Day'
                                          color='primary'
                                          size='small'
                                          icon={<FlightLandIcon />}
                                       />
                                    ) : null}
                                 </Box>
                              </Box>

                              {!hasEvents ? (
                                 <Box sx={{ textAlign: 'center', py: 2 }}>
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{ fontStyle: 'italic' }}
                                    >
                                       No events planned for this day
                                    </Typography>
                                    {!isPastTrip && (
                                       <Button
                                          variant='outlined'
                                          size='small'
                                          sx={{ mt: 1 }}
                                          onClick={() => onDaySelect(index)}
                                       >
                                          Plan This Day
                                       </Button>
                                    )}
                                 </Box>
                              ) : (
                                 <Box>
                                    {/* Images from this day - displayed in a nice grid */}
                                    {dayImages.length > 0 && (
                                       <Box sx={{ mb: 2 }}>
                                          <Box
                                             sx={{
                                                display: 'grid',
                                                gridTemplateColumns:
                                                   dayImages.length === 1
                                                      ? '1fr'
                                                      : dayImages.length === 2
                                                      ? '1fr 1fr'
                                                      : 'repeat(3, 1fr)',
                                                gap: 1,
                                                mb: 1,
                                             }}
                                          >
                                             {dayImages
                                                .slice(0, Math.min(3, dayImages.length))
                                                .map((image, idx) => (
                                                   <Box
                                                      key={idx}
                                                      sx={{
                                                         aspectRatio: '1',
                                                         backgroundImage: `url(${image})`,
                                                         backgroundSize: 'cover',
                                                         backgroundPosition: 'center',
                                                         borderRadius: 1,
                                                         cursor: 'pointer',
                                                         transition: 'transform 0.2s',
                                                         '&:hover': {
                                                            transform: 'scale(1.05)',
                                                         },
                                                      }}
                                                      onClick={() =>
                                                         openImageDialog(dayImages, idx)
                                                      }
                                                   />
                                                ))}
                                          </Box>

                                          {dayImages.length > 3 && (
                                             <Button
                                                size='small'
                                                endIcon={<ImageIcon />}
                                                onClick={() => openImageDialog(dayImages)}
                                                sx={{ mt: 0.5 }}
                                             >
                                                View All {dayImages.length} Photos
                                             </Button>
                                          )}
                                       </Box>
                                    )}

                                    {/* Day highlights */}
                                    <Box sx={{ mb: 2 }}>
                                       {dayAccommodations.length > 0 && (
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                mb: 1,
                                             }}
                                          >
                                             <HotelIcon
                                                fontSize='small'
                                                sx={{ mr: 1, mt: 0.3 }}
                                                color='success'
                                             />
                                             <Box>
                                                <Typography variant='body2' fontWeight='medium'>
                                                   Staying at {dayAccommodations[0].name}
                                                </Typography>
                                                {dayAccommodations[0].address && (
                                                   <Typography
                                                      variant='caption'
                                                      color='text.secondary'
                                                      display='block'
                                                   >
                                                      {dayAccommodations[0].address}
                                                   </Typography>
                                                )}
                                             </Box>
                                          </Box>
                                       )}

                                       {dayTransportation.length > 0 && (
                                          <Box>
                                             {dayTransportation.map((transport, tIdx) => (
                                                <Box
                                                   key={tIdx}
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'flex-start',
                                                      mb: 1,
                                                   }}
                                                >
                                                   {getTransportIcon(transport.type)}
                                                   <Box sx={{ ml: 1 }}>
                                                      <Typography
                                                         variant='body2'
                                                         fontWeight='medium'
                                                      >
                                                         {transport.from}{' '}
                                                         <ArrowForwardIcon
                                                            sx={{ fontSize: 14, mx: 0.5 }}
                                                         />{' '}
                                                         {transport.to}
                                                      </Typography>
                                                      {transport.departureTime && (
                                                         <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            display='block'
                                                         >
                                                            {format(
                                                               new Date(transport.departureTime),
                                                               'h:mm a'
                                                            )}
                                                            {transport.arrivalTime && (
                                                               <>
                                                                  {' '}
                                                                  -{' '}
                                                                  {format(
                                                                     new Date(
                                                                        transport.arrivalTime
                                                                     ),
                                                                     'h:mm a'
                                                                  )}
                                                               </>
                                                            )}
                                                         </Typography>
                                                      )}
                                                   </Box>
                                                </Box>
                                             ))}
                                          </Box>
                                       )}
                                    </Box>

                                    {/* Activities */}
                                    {dayActivities.length > 0 && (
                                       <Box>
                                          <Typography variant='subtitle2' gutterBottom>
                                             {dayActivities.length}{' '}
                                             {dayActivities.length === 1
                                                ? 'Activity'
                                                : 'Activities'}
                                          </Typography>

                                          <Box
                                             sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                             }}
                                          >
                                             {dayActivities.slice(0, 3).map((activity, aIdx) => (
                                                <Box
                                                   key={aIdx}
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      p: 1,
                                                      borderRadius: 1,
                                                      bgcolor: 'background.default',
                                                   }}
                                                >
                                                   {getActivityIcon(activity)}
                                                   <Box sx={{ ml: 1, flex: 1 }}>
                                                      <Typography variant='body2'>
                                                         {activity.startTime && (
                                                            <Typography
                                                               component='span'
                                                               variant='caption'
                                                               color='text.secondary'
                                                               sx={{ mr: 1 }}
                                                            >
                                                               {activity.startTime}
                                                            </Typography>
                                                         )}
                                                         {activity.title}
                                                      </Typography>
                                                   </Box>
                                                </Box>
                                             ))}

                                             {dayActivities.length > 3 && (
                                                <Button
                                                   size='small'
                                                   sx={{ alignSelf: 'flex-start' }}
                                                >
                                                   +{dayActivities.length - 3} more activities
                                                </Button>
                                             )}
                                          </Box>
                                       </Box>
                                    )}

                                    <Button
                                       variant='outlined'
                                       size='small'
                                       sx={{ mt: 2 }}
                                       onClick={() => onDaySelect(index)}
                                       endIcon={<InfoIcon />}
                                    >
                                       View Details
                                    </Button>
                                 </Box>
                              )}
                           </Paper>
                        </Grow>
                     </TimelineContent>
                  </TimelineItem>
               );
            })}
         </Timeline>
      </Box>
   );
};

export default TimelineChronology;
